import {zodResolver} from "@hookform/resolvers/zod";
import type {CreateWorkerPayload, IAddress, UpdateWorkerPayload, Worker} from "@mytour/shared";
import {EmailAlreadyUsedErrorSchema} from "@mytour/shared";
import {FloatingField} from "@mytour/ui/form";
import {Splitter} from "@mytour/ui/layout";
import axios from "axios";
import {useEffect} from "react";
import {Controller, useForm} from "react-hook-form";
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import {z} from "zod";
import {useDepartureAddress} from "../../../../query/departureAddressQueries.js";
import {useCreateWorker, useUpdateWorker} from "../../../../query/teamWorkerQueries.js";
import {WorkerAddressSection} from "./WorkerAddressSection.js";

interface WorkerFormProps {
  isOpen: boolean;
  worker: Worker | null;
  onClose: () => void;
}

// Form validation schema - address is optional but if provided, must be valid
const workerFormSchema = z.object({
  firstName: z.string().min(1, "Le prénom est requis"),
  lastName: z.string().min(1, "Le nom est requis"),
  email: z.string().email("L'email n'est pas valide"),
  // Address is optional as a whole, but if provided, fields must be valid
  address: z
    .object({
      streetAddress: z.string().trim().min(1, "L'adresse est requise"),
      streetAddress2: z.string().optional(),
      city: z.string().trim().min(1, "La ville est requise"),
      zipCode: z
        .string()
        .trim()
        .regex(/^\d{5}$/, "Le code postal doit contenir 5 chiffres"),
      country: z.string().trim().min(1, "Le pays est requis"),
    })
    .optional(),
  hasCustomAddress: z.boolean(),
  canCreateInvoice: z.boolean(),
  canEditCustomer: z.boolean(),
});

export type WorkerFormData = z.infer<typeof workerFormSchema>;

export function WorkerForm({isOpen, worker, onClose}: WorkerFormProps) {
  const {createWorker, isCreatingWorker} = useCreateWorker();
  const {updateWorker, isUpdatingWorker} = useUpdateWorker();
  const {departureAddress, isDepartureAddressLoading} = useDepartureAddress();

  const isEditing = !!worker;
  const isLoading = isCreatingWorker || isUpdatingWorker || isDepartureAddressLoading;

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: {errors},
  } = useForm<WorkerFormData>({
    resolver: zodResolver(workerFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      address: undefined,
      hasCustomAddress: false,
      canCreateInvoice: false,
      canEditCustomer: false,
    },
  });

  const hasCustomAddress = watch("hasCustomAddress");

  // biome-ignore lint/correctness/useExhaustiveDependencies: isOpen resets form when modal re-opens
  useEffect(() => {
    if (worker) {
      if (!departureAddress) {
        return;
      }
      const {departureAddress: current} = departureAddress;
      reset({
        firstName: worker.firstName,
        lastName: worker.lastName,
        email: worker.email,
        address: {
          streetAddress: worker.address.streetAddress,
          streetAddress2: worker.address.streetAddress2 ?? "",
          city: worker.address.city,
          zipCode: worker.address.zipCode,
          country: worker.address.country,
        },
        hasCustomAddress: departureAddress ? !addressesMatch(worker.address, current) : true,
        canCreateInvoice: worker.canCreateInvoice,
        canEditCustomer: worker.canEditCustomer,
      });
    } else {
      reset({
        firstName: "",
        lastName: "",
        email: "",
        address: undefined,
        hasCustomAddress: false,
        canCreateInvoice: false,
        canEditCustomer: false,
      });
    }
  }, [worker, reset, isOpen, departureAddress]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {isEditing ? "Modifier l'intervenant" : "Ajouter un intervenant"}
      </ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          {errors.root?.server?.message && (
            <Alert color="danger" fade={false}>
              {errors.root.server.message}
            </Alert>
          )}
          <Splitter>
            <FloatingField
              name="firstName"
              label="Prénom"
              control={control}
              error={errors.firstName}
              required
            />
            <FloatingField
              name="lastName"
              label="Nom"
              control={control}
              error={errors.lastName}
              required
            />
          </Splitter>

          <FloatingField
            name="email"
            label="Email"
            control={control}
            error={errors.email}
            required
          />

          <hr />
          <h6>Adresse de départ</h6>

          <WorkerAddressSection
            control={control}
            errors={errors}
            hasCustomAddress={hasCustomAddress}
            onAddressToggle={handleAddressToggle}
          />

          <hr />
          <h6>Autorisations</h6>

          <FormGroup check>
            <Controller
              name="canCreateInvoice"
              control={control}
              render={({field}) => (
                <>
                  <Input
                    type="checkbox"
                    id="canCreateInvoice"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                  <Label for="canCreateInvoice" check>
                    Autoriser la création de factures
                  </Label>
                </>
              )}
            />
          </FormGroup>

          <FormGroup check>
            <Controller
              name="canEditCustomer"
              control={control}
              render={({field}) => (
                <>
                  <Input
                    type="checkbox"
                    id="canEditCustomer"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                  <Label for="canEditCustomer" check>
                    Autoriser la modification des clients
                  </Label>
                </>
              )}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button color="primary" type="submit" disabled={isLoading}>
            {isLoading ? "Enregistrement..." : isEditing ? "Enregistrer" : "Créer"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );

  function handleAddressToggle(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.currentTarget.checked) {
      setValue("address", undefined);
      setValue("hasCustomAddress", false);
    } else {
      setValue("address", {
        streetAddress: "",
        streetAddress2: "",
        city: "",
        zipCode: "",
        country: "France",
      });
      setValue("hasCustomAddress", true);
    }
  }

  async function onSubmit(data: WorkerFormData) {
    clearErrors();
    try {
      if (isEditing) {
        const payload: UpdateWorkerPayload = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          address: data.address
            ? {
                streetAddress: data.address.streetAddress,
                streetAddress2: data.address.streetAddress2 || undefined,
                city: data.address.city,
                zipCode: data.address.zipCode,
                country: data.address.country,
              }
            : departureAddress?.departureAddress,
          canCreateInvoice: data.canCreateInvoice,
          canEditCustomer: data.canEditCustomer,
        };
        await updateWorker({workerId: worker.id, payload});
      } else {
        const payload: CreateWorkerPayload = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          address: data.address
            ? {
                streetAddress: data.address.streetAddress,
                streetAddress2: data.address.streetAddress2 || undefined,
                city: data.address.city,
                zipCode: data.address.zipCode,
                country: data.address.country,
              }
            : departureAddress?.departureAddress,
          canCreateInvoice: data.canCreateInvoice,
          canEditCustomer: data.canEditCustomer,
        };
        await createWorker(payload);
      }
      onClose();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const emailError = EmailAlreadyUsedErrorSchema.safeParse(error.response.data);
        if (emailError.success) {
          setError("email", {type: "server", message: emailError.data.message});
          return;
        }
      }
      const message =
        axios.isAxiosError<{message?: unknown}>(error) &&
        error.response?.status === 400 &&
        typeof error.response.data?.message === "string"
          ? error.response.data.message
          : "L'intervenant n'a pas pu être enregistré. Veuillez réessayer.";
      setError("root.server", {type: "server", message});
    }
  }
}

function addressesMatch(a: Worker["address"], b: IAddress): boolean {
  return (
    a.streetAddress.trim() === b.streetAddress.trim() &&
    (a.streetAddress2 ?? "").trim() === (b.streetAddress2 ?? "").trim() &&
    a.zipCode.trim() === b.zipCode.trim() &&
    a.city.trim() === b.city.trim()
  );
}
